import type {
  RoleCreateInput,
  RoleUpdateInput,
} from "@/modules/roles/domain/contracts";
import type { PermissionId } from "@/modules/roles/domain/contracts/permissions";
import {
  Permissions,
  getActionLabel,
  getResourceLabel,
} from "@/modules/roles/domain/contracts/permissions";
import { FormRoot, FormInput, FormTextarea, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";


import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

const RoleFormSchema = z.object({
  name: z.string().min(1, "Required"),
  description: z.string().nullable().optional(),
  permissions: z.array(z.string()),
});

export type RoleFormValues = z.infer<typeof RoleFormSchema>;

export function RoleForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<RoleFormValues>;
  onSubmit: (values: RoleCreateInput | RoleUpdateInput) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<RoleFormValues>({
    resolver: zodResolver(RoleFormSchema),
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
      ...initialValues,
    },
  });

  const selected = methods.watch("permissions") as string[];
  const [searchQuery, setSearchQuery] = useState("");

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchQuery(val);
  }, 300);

  // Transform Permissions into unified structure
  type PermissionGroup = {
    key: string;
    resource: string;
    actions: Record<string, string>;
    parentKey?: string;
  };

  const buildPermissionGroups = (): PermissionGroup[] => {
    const groups: PermissionGroup[] = [];
    const {
      pos: posPermissions,
      reports: reportsPermissions,
      ...regularPermissions
    } = Permissions;

    // Add regular permissions (flat structure)
    Object.entries(regularPermissions).forEach(([resource, actions]) => {
      if (typeof actions === "object" && actions !== null) {
        groups.push({
          key: resource,
          resource,
          actions: actions as Record<string, string>,
        });
      }
    });

    // Add reports permissions (nested structure)
    if (reportsPermissions && typeof reportsPermissions === "object") {
      Object.entries(reportsPermissions).forEach(([key, value]) => {
        if (typeof value === "string") {
          // Handle direct string values like reports.export
          groups.push({
            key: `reports-${key}`,
            resource: key,
            actions: { [key]: value },
            parentKey: "reports",
          });
        } else if (typeof value === "object" && value !== null) {
          // Handle nested objects like reports.sales.read
          // Ensure all values are strings
          const actions: Record<string, string> = {};
          Object.entries(value).forEach(([actionKey, actionValue]) => {
            if (typeof actionValue === "string") {
              actions[actionKey] = actionValue;
            }
          });
          if (Object.keys(actions).length > 0) {
            groups.push({
              key: `reports-${key}`,
              resource: key,
              actions,
              parentKey: "reports",
            });
          }
        }
      });
    }

    // Add POS permissions (nested structure)
    if (posPermissions) {
      Object.entries(posPermissions).forEach(([resource, actions]) => {
        if (typeof actions === "object" && actions !== null) {
          groups.push({
            key: `pos-${resource}`,
            resource,
            actions: actions as Record<string, string>,
            parentKey: "pos",
          });
        }
      });
    }

    return groups;
  };

  const allPermissionGroups = buildPermissionGroups();

  // Filter permissions based on search query
  const filteredPermissionGroups = allPermissionGroups.filter((group) => {
    if (!searchQuery) return true;
    const resourceLabel = getResourceLabel(group.resource);
    const actionLabels = Object.keys(group.actions).map((action) => {
      // Handle nested action keys like "sales.read" or "export"
      const actionKey = action.includes(".")
        ? (action.split(".")[1] ?? action)
        : action;
      return getActionLabel(actionKey);
    });
    const searchLower = searchQuery.toLowerCase();
    return (
      resourceLabel.toLowerCase().includes(searchLower) ||
      actionLabels.some((label) => label.toLowerCase().includes(searchLower)) ||
      (group.parentKey === "pos" &&
        "pos".toLowerCase().includes(searchLower)) ||
      (group.parentKey === "reports" &&
        "reports".toLowerCase().includes(searchLower))
    );
  });

  // Group permissions by parent
  const posGroups = filteredPermissionGroups.filter(
    (g) => g.parentKey === "pos",
  );
  const reportsGroups = filteredPermissionGroups.filter(
    (g) => g.parentKey === "reports",
  );
  const regularGroups = filteredPermissionGroups.filter((g) => !g.parentKey);

  const toggleMany = (ids: string[], checked: boolean) => {
    const current = (selected ?? []).filter(
      (id): id is string => id !== null && id !== undefined && id !== "",
    );
    if (checked) {
      const set = new Set([...current, ...ids]);
      methods.setValue("permissions", Array.from(set));
    } else {
      methods.setValue(
        "permissions",
        current.filter((id) => !ids.includes(id)),
      );
    }
  };

  const toggleOne = (id: string, checked: boolean) => {
    const current = (selected ?? []).filter(
      (id): id is string => id !== null && id !== undefined && id !== "",
    );
    if (checked) {
      if (!current.includes(id))
        methods.setValue("permissions", [...current, id]);
    } else {
      methods.setValue(
        "permissions",
        current.filter((x) => x !== id),
      );
    }
  };

  // Unified component for rendering permission group
  const renderPermissionGroup = (
    group: PermissionGroup,
    level: "top" | "nested" = "top",
  ) => {
    // Ensure all IDs are strings
    const groupIds = Object.values(group.actions).filter(
      (id): id is string => typeof id === "string" && id !== "",
    );
    const allChecked = groupIds.every((id) => selected?.includes(id));
    const someChecked =
      groupIds.some((id) => selected?.includes(id)) && !allChecked;
    const friendlyResource = getResourceLabel(group.resource);
    const selectedCount = groupIds.filter((id) =>
      selected?.includes(id),
    ).length;
    const totalCount = groupIds.length;

    const isNested = level === "nested";
    const containerClass = isNested
      ? "rounded-md border bg-background"
      : "rounded-md border";
    const triggerPadding = isNested ? "p-2" : "p-3";
    const iconSize = isNested ? "h-3 w-3" : "h-4 w-4";
    const labelClass = isNested
      ? "cursor-pointer font-medium text-sm"
      : "cursor-pointer font-medium";
    const badgeClass = isNested ? "ml-auto text-xs" : "ml-auto";
    const contentBg = isNested ? "bg-muted/20" : "bg-muted/30";
    const contentPadding = isNested ? "p-2" : "p-3";
    const actionLabelClass = isNested
      ? "cursor-pointer text-xs"
      : "cursor-pointer text-sm";

    return (
      <Collapsible
        key={group.key}
        defaultOpen={false}
        className="group/collapsible"
      >
        <div className={containerClass}>
          <CollapsibleTrigger className="w-full">
            <div
              className={`flex w-full items-center justify-between ${triggerPadding} hover:bg-muted/50`}
            >
              <div className="flex items-center gap-2">
                <ChevronDown
                  className={`${iconSize} transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180`}
                />
                <Checkbox
                  id={`perm-group-${group.key}`}
                  checked={allChecked}
                  onCheckedChange={(val) => toggleMany(groupIds, Boolean(val))}
                  aria-checked={someChecked ? "mixed" : allChecked}
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor={`perm-group-${group.key}`}
                  className={labelClass}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                    }
                  }}
                >
                  {friendlyResource}
                </label>
              </div>
              <Badge variant="secondary" className={badgeClass}>
                {selectedCount}/{totalCount}
              </Badge>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div
              className={`grid gap-2 border-t ${contentBg} ${contentPadding} sm:grid-cols-2 md:grid-cols-3`}
            >
              {Object.entries(group.actions).map(([action, id]) => {
                // Ensure id is a string, not an object
                const idStr = typeof id === "string" ? id : String(id ?? "");
                if (!idStr || idStr === "") return null;
                const checked = selected?.includes(idStr) ?? false;
                const inputId = `perm-${group.key}-${idStr}`;
                const friendlyAction = getActionLabel(action);
                // For nested groups (reports, pos), show resource name with action
                const displayLabel =
                  group.parentKey && group.resource !== group.parentKey
                    ? `${friendlyAction} ${getResourceLabel(group.resource)}`
                    : `${friendlyAction} ${friendlyResource}`;
                return (
                  <div key={idStr} className="flex items-center gap-2">
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      onCheckedChange={(val) => toggleOne(idStr, Boolean(val))}
                    />
                    <label htmlFor={inputId} className={actionLabelClass}>
                      {displayLabel}
                    </label>
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  };

  // Helper function to render nested permission groups (POS, Reports)
  const renderNestedGroup = (
    groups: PermissionGroup[],
    parentKey: string,
    parentLabel: string,
  ) => {
    if (groups.length === 0) return null;

    return (
      <Collapsible
        key={parentKey}
        defaultOpen={false}
        className="group/collapsible"
      >
        <div className="rounded-md border border-primary/20">
          <CollapsibleTrigger className="w-full">
            <div className="flex w-full items-center justify-between p-3 hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                <Checkbox
                  id={`perm-group-${parentKey}`}
                  checked={groups.every((group) => {
                    const groupIds = Object.values(group.actions).filter(
                      (id): id is string => typeof id === "string" && id !== "",
                    );
                    return groupIds.every((id) => selected?.includes(id));
                  })}
                  onCheckedChange={(val) => {
                    const allIds = groups.flatMap((group) =>
                      Object.values(group.actions).filter(
                        (id): id is string =>
                          typeof id === "string" && id !== "",
                      ),
                    );
                    toggleMany(allIds, Boolean(val));
                  }}
                  aria-checked={
                    groups.some((group) => {
                      const groupIds = Object.values(group.actions).filter(
                        (id): id is string =>
                          typeof id === "string" && id !== "",
                      );
                      return groupIds.some((id) => selected?.includes(id));
                    }) &&
                    !groups.every((group) => {
                      const groupIds = Object.values(group.actions).filter(
                        (id): id is string =>
                          typeof id === "string" && id !== "",
                      );
                      return groupIds.every((id) => selected?.includes(id));
                    })
                      ? "mixed"
                      : groups.every((group) => {
                          const groupIds = Object.values(group.actions).filter(
                            (id): id is string =>
                              typeof id === "string" && id !== "",
                          );
                          return groupIds.every((id) => selected?.includes(id));
                        })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor={`perm-group-${parentKey}`}
                  className="cursor-pointer font-medium"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                    }
                  }}
                >
                  {parentLabel}
                </label>
              </div>
              <Badge variant="secondary" className="ml-auto">
                {groups.reduce((acc, group) => {
                  const groupIds = Object.values(group.actions).filter(
                    (id): id is string => typeof id === "string" && id !== "",
                  );
                  return (
                    acc + groupIds.filter((id) => selected?.includes(id)).length
                  );
                }, 0)}
                /
                {groups.reduce((acc, group) => {
                  const groupIds = Object.values(group.actions).filter(
                    (id): id is string => typeof id === "string" && id !== "",
                  );
                  return acc + groupIds.length;
                }, 0)}
              </Badge>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-2 border-t bg-muted/20 p-3">
              {groups.map((group) => renderPermissionGroup(group, "nested"))}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  };

  return (
    <FormRoot<RoleFormValues>
      methods={methods}
      onSubmit={(vals) => {
        // Filter out null, undefined, and invalid permissions
        const validPermissions = vals.permissions.filter(
          (id): id is string =>
            id !== null &&
            id !== undefined &&
            id !== "" &&
            typeof id === "string",
        ) as PermissionId[];

        onSubmit({
          name: vals.name,
          description: vals.description ?? null,
          permissions: validPermissions,
        });
      }}
      className="space-y-4"
    >
      <div data-tourid="form-name">
        <FormInput
          name="name"
          label="ຊື່ບົດບາດ"
          requiredMark
          placeholder="ຕົວຢ່າງ: Admin"
        />
      </div>
      <div data-tourid="form-description">
        <FormTextarea
          name="description"
          label="ຄໍາອະທິບາຍ"
          placeholder="ຕົວຢ່າງ: ບົດບາດ Admin ທີ່ສາມາດເຂົ້າເຖິງທຸກຟີຈເຈີ"
        />
      </div>
      <div>
        <div className="mb-2 block text-sm">ສິດທິ</div>
        <div className="mb-3" data-tourid="form-permissions-search">
          <Input
            placeholder="ຄົ້ນຫາສິດທິ..."
            onChange={(e) => debouncedSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <ScrollArea
          className="max-h-96 overflow-auto rounded-md border p-4"
          data-tourid="form-permissions"
        >
          <div className="space-y-3 pr-1">
            {/* Regular permissions */}
            {regularGroups.map((group) => renderPermissionGroup(group, "top"))}

            {/* Reports permissions - nested groups */}
            {renderNestedGroup(
              reportsGroups,
              "reports",
              getResourceLabel("reports"),
            )}

            {/* POS permissions - nested groups */}
            {renderNestedGroup(posGroups, "pos", "POS (Point of Sale)")}

            {filteredPermissionGroups.length === 0 && (
              <div className="py-8 text-center text-muted-foreground text-sm">
                ບໍ່ພົບສິດທິທີ່ຕົງກັບການຄົ້ນຫາ
              </div>
            )}
          </div>
        </ScrollArea>

        {JSON.stringify(methods.formState.errors.permissions)}
      </div>
      <div className="flex justify-end gap-2" data-tourid="form-submit">
        <Button type="submit" disabled={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
